import { withLayouts } from 'react-dx'
import { useLingui } from '@lingui/react/macro'

import DefaultLayout from '../../_layout'
import RootLayout from '../../_layout/layouts/RootLayout'
import SiteSpecificContextLayout from '../../_layout/layouts/SiteSpecificContextLayout'
import AntdLayout from '../../_layout/layouts/AntdLayout'
import { transformMusicData } from '../../_components/MusicList/_helpers/data'
import MusicDetailComponent from '../../_components/MusicDetail'

import type { RouterOutput } from '@loc/server'
import type { MusicListDataItem } from '../../ai-music-generator/_types/trpc'
import type { TNextPagePro } from '@/types/global'
import type { InferGetServerSidePropsType } from 'next'

import { getServerSideData } from '@/helpers/server-data'
import { NotFoundError } from '@/helpers/server-data/errors'
import getDataFromDehydrate from '@/helpers/server-data/getDataFromDehydrate'

export const Page: TNextPagePro<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  const router = useRouter()
  const { data: _data } = api.music_v2.getMusicDetail.useQuery({
    uuid: router.query.id as string,
  })
  const initAudioPlayer = useGlobalAudioPlayer((state) => state.init)
  const currentMusicId = useGlobalAudioPlayer((state) => state.meta?.id)

  const data = _data?.musicDetail
    ? transformMusicData(_data.musicDetail as MusicListDataItem, true)
    : null

  useEffect(() => {
    if (!data?.id || !data?.src || currentMusicId === data.id) {
      return
    }
    initAudioPlayer(data)
  }, [currentMusicId, data, initAudioPlayer])

  if (!data) {
    return null
  }

  return <MusicDetailComponent data={data} />
}

Page.noPageKey = true

const LayoutsPage = withLayouts(
  Page,
  [
    (props) => {
      return (
        <DefaultLayout
          {...props}
          minHScreen
          main={{
            className: 'bg-f-bg-base',
          }}
          sideBar={{}}
          footer={null}
        />
      )
    },
    (props) => {
      const { seoTitle } = useConfig()
      const { t } = useLingui()
      const router = useRouter()
      const { data: _data } = api.music_v2.getMusicDetail.useQuery({
        uuid: router.query.id as string,
      })

      const data = transformMusicData(
        _data?.musicDetail as MusicListDataItem,
        true,
      )

      // const { data } =
      //   usePageProps<InferGetServerSidePropsType<typeof getServerSideProps>>()
      const musicTitle = data.title ?? t`Untitled`

      return (
        <RootLayout
          seo={{
            title: seoTitle(`${musicTitle} - Royalty Free Song Free Download`),
            description: `Discover "${musicTitle}", an enchanting royalty-free song available for free download on Tad AI. Download now and elevate your work with captivating music!`,
            nofollow: true,
            noindex: true,
            openGraph: {
              images: [
                {
                  url: data.cover,
                },
              ],
            },
          }}
          {...props}
        />
      )
    },
    (props) => {
      return <SiteSpecificContextLayout {...props} />
    },
    AntdLayout,
  ],
  {
    propertiesHoist: ['noPageKey'],
  },
)

export default LayoutsPage

// eslint-disable-next-line react-refresh/only-export-components
export const getServerSideProps = getServerSideData<{ id: string }>({
  cacheControl: ['max-age=60', 'stale-while-revalidate=60'],
})(async (_, ctx, helpers) => {
  const musicUuid = ctx.params?.id

  const trpcHelpers = await helpers.getTrpcHelpers()

  if (!musicUuid) {
    throw new NotFoundError('No music id')
  }

  await trpcHelpers.music_v2.getMusicDetail.prefetch({
    uuid: musicUuid,
  })

  const musicDetailRes = getDataFromDehydrate(trpcHelpers.dehydrate(), [
    'music_v2',
    'getMusicDetail',
  ])

  const musicDetailData = (
    musicDetailRes as RouterOutput['music_v2']['getMusicDetail']
  )?.musicDetail

  if (!musicDetailData) {
    throw new NotFoundError('No music detail')
  }

  return { trpcState: trpcHelpers.dehydrate() }
})
